/* eslint-disable react-refresh/only-export-components -- shared chrome module: hook and presentational components co-located */

/**
 * Shared chrome utilities for record-title inline editors.
 *
 * Exports:
 * - `useTitleEditorAffordance` — hook managing the focus-on-edit effect,
 *   click/keydown handlers, and trigger-ref callback.
 * - `TitleAnnouncementRegion` — aria-live wrapper for save announcements.
 * - `TitleReadModeContent` — read-mode JSX with pencil-icon-on-hover.
 */

import { Edit03 } from '@untitledui/icons';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { TruncatedText } from '@/lib/base/truncated-text';
import { FIELD_CHROME_NEUTRALIZE } from '@/lib/crm/inline-edit/chromeOverrides';
import { cx } from '@/utils/cx';

// ─── Title typography / edit-mode chrome ────────────────────────────────────

/**
 * The record-header title typography — the single source for BOTH the
 * read-mode text and the edit-mode input, so entering edit mode cannot
 * restyle the glyphs. Change it here and both modes move together.
 */
export const TITLE_TEXT_CLASSES =
  'text-lg md:text-xl font-semibold text-fg-primary';

/**
 * Edit-mode field overrides for the record-header title editors. Pairs with
 * `textDisplay='card'` on CrmFieldValueInput: that renders the bare <input>
 * these `[&_input]` selectors (and the base Input's HTMLInputElement focus
 * ref) are bound to — the default 'form' textarea would ignore all of this.
 *
 * Zero-movement contract with `TitleReadModeContent`: the input carries the
 * exact read-mode typography and no padding/ring/background, so the glyphs
 * render in the same position, size, and weight in both modes.
 */
export const TITLE_EDITOR_FIELD_OVERRIDES = cx(
  FIELD_CHROME_NEUTRALIZE,
  // No padding — the read-mode span has none, and any padding here would
  // shift the glyphs on entering edit mode. Content-sized so the input's box
  // equals the text box (max-w-full stops a long draft overflowing the
  // header). Callers add their own `[&_input]:min-w-*` floor: the floor keeps
  // an emptied field visible/clickable, but any floor wider than the text
  // shifts whatever renders AFTER the input — so the single-name editor
  // (nothing after the input) uses a comfortable min-w-12 while the contact
  // pair (last name sits after the first field) uses a minimal min-w-4.
  '[&_input]:p-0 [&_input]:w-auto [&_input]:[field-sizing:content]',
  '[&_input]:max-w-full',
  // Title typography on the input itself (wins over the size-prop type
  // classes on the element — descendant selector carries higher specificity).
  '[&_input]:text-lg md:[&_input]:text-xl [&_input]:font-semibold [&_input]:text-fg-primary'
);

// ─── useTitleEditorAffordance ────────────────────────────────────────────────

interface UseTitleEditorAffordanceOptions {
  isEditing: boolean;
  containerRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  startEdit: () => void;
}

interface TitleEditorAffordance {
  setTriggerRef: (el: HTMLSpanElement | null) => void;
  handleTitleClick: (e: MouseEvent) => void;
  handleTitleKeyDown: (e: KeyboardEvent<HTMLSpanElement>) => void;
}

/**
 * Provides the shared affordance logic for record-title inline editors:
 * focus-on-edit layout effect (with click-point caret placement),
 * click/keydown title handlers, and the callback ref that restores focus
 * after commit/cancel.
 *
 * Previously duplicated in RecordTitleSingleNameEditor (mirroring
 * InlineEditableFieldRow:431-440) and RecordTitleContactEditor (mirroring
 * InlineEditableFieldRow.tsx:422-440); now centralised here.
 */
export function useTitleEditorAffordance({
  isEditing,
  containerRef,
  triggerRef,
  startEdit,
}: UseTitleEditorAffordanceOptions): TitleEditorAffordance {
  // Character offset captured at click time, applied to the edit-mode field
  // on the next focus effect. null = no click point (keyboard entry, pencil
  // click) → caret goes to the end.
  const pendingCaretOffsetRef = useRef<number | null>(null);

  // Focus the field the moment edit mode opens and drop the caret at the
  // clicked character. CrmFieldValueInput does not auto-focus. Mirrors
  // InlineEditableFieldRow:431-440 (focus) and NoteCard's title
  // click-to-edit (caret mapping — read text and edit fields share the same
  // string and typography, so offsets map 1:1).
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    const container = containerRef.current;
    if (!container || container.contains(document.activeElement)) {
      return;
    }
    const offset = pendingCaretOffsetRef.current;
    pendingCaretOffsetRef.current = null;
    const fields = Array.from(
      container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        'input:not([type="hidden"]), textarea'
      )
    );
    if (fields.length === 0) {
      container
        .querySelector<HTMLElement>(
          'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
        )
        ?.focus({ preventScroll: true });
      return;
    }
    // Resolve which field owns the offset. Contact titles render
    // "first last" as two fields; the read-mode string joins them with a
    // one-space separator only when both parts are non-empty
    // (composePersonName), so offsets past the first part (+separator)
    // belong to the second field.
    let field = fields[0];
    let caret = offset ?? field.value.length;
    if (offset != null && fields.length > 1) {
      const first = fields[0].value;
      if (!first) {
        field = fields[1];
        caret = offset;
      } else if (offset > first.length && fields[1].value) {
        field = fields[1];
        caret = offset - first.length - 1;
      }
    }
    caret = Math.max(0, Math.min(caret, field.value.length));
    field.focus({ preventScroll: true });
    field.setSelectionRange(caret, caret);
  }, [isEditing, containerRef]);

  const handleTitleClick = useCallback(
    (e: MouseEvent) => {
      // Capture the character offset under the pointer BEFORE the read span
      // unmounts (NoteCard.tsx title pattern). Guarded to the trigger span:
      // a pencil-icon click has no meaningful text offset and falls back to
      // caret-at-end.
      const caretPos = document.caretPositionFromPoint?.(e.clientX, e.clientY);
      pendingCaretOffsetRef.current =
        caretPos && triggerRef.current?.contains(caretPos.offsetNode)
          ? caretPos.offset
          : null;
      startEdit();
    },
    [startEdit, triggerRef]
  );

  const handleTitleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pendingCaretOffsetRef.current = null;
        startEdit();
      }
    },
    [startEdit]
  );

  // Stable callback ref: assigns the span to the mutable ref object that
  // useInlineEdit uses to restore focus after commit/cancel.
  const setTriggerRef = useCallback(
    (el: HTMLSpanElement | null) => {
      triggerRef.current = el;
    },
    [triggerRef]
  );

  // Wrap the returned object in useMemo keyed on the three already-stable
  // members so consumers that pass this object across a prop, dep-array, or
  // context boundary don't lose memoization on every render.
  return useMemo(
    () => ({ setTriggerRef, handleTitleClick, handleTitleKeyDown }),
    [setTriggerRef, handleTitleClick, handleTitleKeyDown]
  );
}

// ─── TitleAnnouncementRegion ─────────────────────────────────────────────────

interface TitleAnnouncementRegionProps {
  announcement: ReactNode;
}

/** aria-live assertive region for save/error announcements. Must render outside
 *  the edit/read branch so announcements survive the isEditing flip. */
export function TitleAnnouncementRegion({
  announcement,
}: TitleAnnouncementRegionProps) {
  return (
    <div aria-live='assertive' className='sr-only' aria-atomic='true'>
      {announcement}
    </div>
  );
}

// ─── TitleReadModeContent ────────────────────────────────────────────────────

interface TitleReadModeContentProps {
  canRename: boolean;
  displayName: string;
  setTriggerRef: (el: HTMLSpanElement | null) => void;
  handleTitleClick: (_e: MouseEvent) => void;
  handleTitleKeyDown: (e: KeyboardEvent<HTMLSpanElement>) => void;
}

/** Read-mode title rendering with pencil-icon-on-hover when rename is allowed. */
export function TitleReadModeContent({
  canRename,
  displayName,
  setTriggerRef,
  handleTitleClick,
  handleTitleKeyDown,
}: TitleReadModeContentProps) {
  if (!canRename) {
    return <TruncatedText className={TITLE_TEXT_CLASSES} text={displayName} />;
  }
  return (
    <div className='group/title flex items-center gap-1 min-w-0'>
      <span
        ref={setTriggerRef}
        role='button'
        tabIndex={0}
        aria-label={`Rename ${displayName}`}
        onClick={handleTitleClick}
        onKeyDown={handleTitleKeyDown}
        className={cx(
          'cursor-text hover:text-fg-secondary transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
          'block min-w-0',
          // Editability affordance: grey wash behind the title on hover/focus
          // (InlineEditableFieldRow's read-row treatment). Padding extends the
          // wash beyond the glyphs; the matching negative margins cancel it so
          // the glyphs — and the header layout — do not move.
          '-mx-1.5 -my-1 rounded-md px-1.5 py-1',
          // focus-within, not focus-visible: the `group/title` wrapper is not
          // itself focusable — the inner role="button" span is — so
          // `group-focus-visible/title` (which needs the wrapper to be
          // :focus-visible) never matches on keyboard focus. focus-within
          // matches when the descendant span is focused.
          'group-hover/title:bg-primary_hover group-focus-within/title:bg-primary_hover'
        )}
      >
        {/* Non-focusable: this span is already inside the role="button" rename
            trigger above, so a focusable truncated span would add a second,
            redundant tab stop. The tooltip still shows on pointer hover. */}
        <TruncatedText
          className={TITLE_TEXT_CLASSES}
          text={displayName}
          focusable={false}
        />
      </span>
      <span
        aria-hidden='true'
        onClick={handleTitleClick}
        className='hidden group-hover/title:block group-focus-within/title:block shrink-0 cursor-pointer'
      >
        <Edit03 className='size-3.5 text-fg-quaternary' />
      </span>
    </div>
  );
}
